import {Typography} from '@mui/material';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import classNames from 'classnames';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import {observer} from 'mobx-react';
import React, {useEffect, useMemo, useState} from 'react';
import StrapiApi from '../../api/strapiApi/StrapiApi';
import {prodMode} from '../../config';
import {actions, categories, eventsCustom, pushCustomEvent, pushEvent} from '../../helpers/analyticsEvents';
import {emailDomainValidator, emailValidator, nameValidator, phoneValidator} from '../../helpers/validators';
import linkStyles from '../../slidesParts/FormForApprove/FormForApprove.module.scss';
import ButtonNext from '../ButtonNext/ButtonNext';
import CheckBox from '../CheckBox/CheckBox';
import ResponsiveDrawer from '../CustomerInformation/ResponsiveDrawer';
import Input from '../Input/Input';
import InputDatePicker from '../Input/InputDatePicker';
import InputMasked from '../Input/InputMasked';
import Select from '../Select/Select';
import {useStore} from '../StoreProvider';
import {privacyPolicyList} from './constants';
import styles from './CustomerInformation.module.scss';

dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

const isValidDateOfBirth = (val: string) => {
    if (typeof val !== 'string' || val.trim().length !== 10) {
        return false;
    }

    const date = dayjs(val, 'MM/DD/YYYY');
    return date.isValid() && date.format('MM/DD/YYYY') === val && date < dayjs() && dayjs().diff(date, 'year') < 120;
};

type CustomerInformationProps = {
    onNextClick: () => Promise<void>;
};

const sxProps = {
    title: {
        fontSize: {xs: 48, md: 72},
        opacity: 0.9,
        lineHeight: {xs: '120%', md: '110%'},
    },
    subtitle: {
        mt: 2,
        letterSpacing: {xs: '4%', md: 0},
    },
    form: {
        pt: {xs: 3, md: 2},
        pb: {xs: 3, md: 0},
    },
};

const LeadForm: React.FC<CustomerInformationProps> = ({onNextClick}) => {
    const store = useStore();
    const [email, setEmail] = store.userData.getField('email');
    const [emailErr, setEmailErr] = useState<string>('');
    const [phoneError, setPhoneError] = useState<string>('');
    const [phone, setPhone] = store.userData.getField('phone');
    const [consent, setConsent] = store.userData.getField('consent');
    const [consentErr] = useState<string>('');
    const [firstName, setFirstName] = store.userData.getField('firstName');
    const [lastName, setLastName] = store.userData.getField('lastName');
    const [firstNameErr, setFirstNameErr] = useState<string>('');
    const [lastNameErr, setLastNameErr] = useState<string>('');
    const [genderError, setGenderError] = useState<string>('');
    const [dateOfBirthErr, setDateOfBirthErr] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [activeDrawer, setActiveDrawer] = useState<{
        isOpen: boolean;
        title: string;
        content: string;
        href: string;
    }>({
        isOpen: false,
        title: '',
        content: '',
        href: '',
    });

    const [policies, setPolicies] = useState([]);
    const phase = store.userData.phase1;

    const isNextButtonEnabled = useMemo(() => {
        return !!(
            emailValidator(email) &&
            emailDomainValidator(email) &&
            phoneValidator(phone.replace(/[ ()]/g, '')) &&
            firstName.trim().length &&
            lastName.trim().length &&
            nameValidator(firstName) &&
            nameValidator(lastName) &&
            phase.gender !== '' &&
            isValidDateOfBirth(phase.dateOfBirth) &&
            consent
        );
    }, [email, firstName, lastName, phone, phase.gender, phase.dateOfBirth, consent]);

    useEffect(() => {
        const fetchPoliciesAndTerms = async () => {
            try {
                const [respPolicies, respTerms] = await Promise.all([
                    StrapiApi.getPolicies(),
                    StrapiApi.getTermsAndConditions(),
                ]);

                const terms = respTerms?.data?.attributes?.Paragraph[0] ?? {};
                const policies = respPolicies?.data ?? [];

                setPolicies([
                    ...policies,
                    {
                        attributes: {
                            title: terms.title,
                            content: terms.description,
                        },
                    },
                ]);
            } catch (error) {
                console.error('Error fetching policies and terms:', error);
            }
        };

        fetchPoliciesAndTerms();
    }, []);

    useEffect(() => {
        if (!activeDrawer.isOpen) {
            store.uiStore.componentsStore.updateField('consentActiveDrawer', false);
        }
    }, [activeDrawer.isOpen]);

    const closeDrawer = () => {
        setActiveDrawer({isOpen: false, title: '', content: '', href: ''});
        store.uiStore.componentsStore.updateField('consentActiveDrawer', false);
    };

    function convertDoBBack(dob: string) {
        const date = new Date(dob);
        const day = ('0' + date.getDate()).slice(-2);
        const month = ('0' + (date.getMonth() + 1)).slice(-2);
        const year = date.getFullYear();

        return `${year}-${month}-${day}`;
    }
    const validateEmail = () => {
        if (!emailValidator(email)) setEmailErr('Invalid email');
        if (!emailDomainValidator(email)) {
            setEmailErr('Invalid email domain');
        }
    };
    const validatePhone = () => {
        if (!phoneValidator(phone.replace(/[ ()]/g, ''))) setPhoneError('Invalid phone');
    };
    const validateFirstname = () => {
        if (!firstName.trim().length) setFirstNameErr('This field can not be empty');
        if (!nameValidator(firstName)) setFirstNameErr('Only letters, dashes, and dots are allowed');
    };
    const validateLastname = () => {
        if (!lastName.trim().length) setLastNameErr('This field can not be empty');
        if (!nameValidator(lastName)) setLastNameErr('Only letters, dashes, and dots are allowed');
    };
    const validateGender = () => {
        if (!phase.gender?.trim().length) setGenderError('This field can not be empty');
        else setGenderError('');
    };
    const validateDateOfBirth = () => {
        if (!phase.dateOfBirth.trim().length) {
            setDateOfBirthErr('This field can not be empty');
        } else {
            const date = dayjs(phase.dateOfBirth, 'MM/DD/YYYY');
            if (date.format('MM/DD/YYYY') !== phase.dateOfBirth || date > dayjs() || dayjs().diff(date, 'year') > 120) {
                setDateOfBirthErr('Incorrect date');
            } else {
                setDateOfBirthErr('');
            }
        }
    };

    const saveForm = async () => {
        setIsLoading(true);

        await onNextClick();
        if (prodMode) {
            pushCustomEvent(eventsCustom.submitContactForm, {
                email: email.trim().toLowerCase(),
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                phone: '1' + phone.replace(/[ ()-]/g, ''),
                gender: phase.gender.trim().charAt(0),
                dob: store.userData.phase1.dateOfBirth.split('/').reverse().join(''),
            });
            pushCustomEvent(eventsCustom.addFullname);
        }
        store.userData.updateField('email', email.trim().toLowerCase());
        store.userData.updateField('firstName', firstName.trim());
        store.userData.updateField('lastName', lastName.trim());
        store.userData.updateField('consent', consent);
        store.userData.updateField('consentTimestamp', new Date().getTime());

        // the user has consented. Send consent data to the backend
        const dob = convertDoBBack(store.userData.phase1.dateOfBirth);
        const firstNameFormatted = encodeURIComponent(firstName.trim());
        const lastNameFormatted = encodeURIComponent(lastName.trim());

        const consentRes = await store.userData.phase4.sendConsent(
            firstNameFormatted,
            lastNameFormatted,
            dob,
            store.userData.uuid,
        );

        fetch('/api/leadData', {
            method: 'POST',
            body: JSON.stringify({
                firstName: firstName,
                lastName: lastName,
                phone: phone,
                email: email,
                gender: phase.gender,
                dob: dob,
                utmSource: store.userData.utm,
            }),
        })
            .then((res) => (res.ok ? res.json() : Promise.reject(res)))
            .then((data) => console.log('Transunion data fetched successfully', data))
            .catch((error) => console.log({transunionDataError: error}));

        store.userData.updateField('consentAuthUrl', consentRes?.payload?.consent?.consentAuthUrl);
        store.saveSession();
        // Add GTM event
        pushEvent(categories.preApproval, actions.getApproved);
        store.uiStore.progressStore.toNextSlide();
        setIsLoading(false);
    };

    const selectActiveDrawer = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, id: string, href: string) => {
        e.stopPropagation();

        const policy = policies.find(({attributes}) => attributes.title === id);

        if (policy) {
            setActiveDrawer({
                isOpen: true,
                title: policy.attributes.title,
                content: policy.attributes.content,
                href,
            });
            store.uiStore.componentsStore.updateField('consentActiveDrawer', true);
        } else {
            window.open(href, '_blank');
        }
    };

    const consentLabel = (
        <p data-testid="consent-checkbox-label" data-consent-variant="variant">
            I am the person indicated above, I agree to do business electronically, and I confirm that I have read and
            agree to the{' '}
            {privacyPolicyList.map(({name, id, href}, index, array) => {
                let punctuation: string;
                if (index === 0) punctuation = ', ';
                else if (index === 1) punctuation = '. ';
                else if (index === array.length - 2) punctuation = ', and ';
                else if (index === array.length - 1) punctuation = '.';
                else punctuation = ', ';
                return (
                    <React.Fragment key={name}>
                        <a
                            target="_blank"
                            rel="noopener noreferrer"
                            className={linkStyles.consentLink}
                            onClick={(e) => selectActiveDrawer(e, id, href)}
                        >
                            {name}
                        </a>
                        {punctuation}
                    </React.Fragment>
                );
            })}{' '}
            By continuing, I agree that{' '}
            <strong>
                <a
                    href="https://verified.inc/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkStyles.consentLink}
                >
                    Verified
                </a>
            </strong>{' '}
            (Amplify&apos;s service provider) and its vendors may receive my personal information and autofill more
            information about me, including my social security number.
            <ResponsiveDrawer activeDrawer={activeDrawer} onClose={closeDrawer} showOpenInSeparateTab={true} />
        </p>
    );

    return (
        <Stack
            component="form"
            onSubmit={(e) => {
                e.preventDefault();
                if (isNextButtonEnabled && !isLoading) saveForm();
            }}
        >
            <Typography component="h1" variant="h1" sx={sxProps.title}>
                Let&apos;s get to your estimate
            </Typography>
            <Typography variant="subtitle1" sx={sxProps.subtitle}>
                To give you the best estimate, we need a few details.
            </Typography>
            <Grid container spacing={{xs: 1, md: 1.5}} sx={sxProps.form}>
                <Grid item xs={12} md={6}>
                    <Input
                        name="firstName"
                        label="First Name"
                        value={firstName}
                        error={firstNameErr}
                        onChange={setFirstName}
                        onBlur={validateFirstname}
                        onFocus={() => setFirstNameErr('')}
                        className={styles.input}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Input
                        label="Last Name"
                        value={lastName}
                        error={lastNameErr}
                        onChange={setLastName}
                        onBlur={validateLastname}
                        onFocus={() => {
                            setLastNameErr('');
                        }}
                        className={styles.input}
                    />
                </Grid>
                <Grid item xs={12} md={12}>
                    <Input
                        label="Email"
                        value={email}
                        error={emailErr}
                        onChange={setEmail}
                        type="email"
                        inputMode={'email'}
                        onBlur={validateEmail}
                        onFocus={() => {
                            setEmailErr('');
                        }}
                        className={styles.input}
                        inputClassName={styles.lowerCase}
                    />
                </Grid>
                <Grid item xs={12} md={12}>
                    <InputMasked
                        type="tel"
                        inputMode={'tel'}
                        placeholder="Phone Number"
                        value={phone}
                        error={phoneError}
                        onChange={setPhone}
                        onBlur={validatePhone}
                        className={classNames(styles.input, styles.phoneInput)}
                        onFocus={() => setPhoneError('')}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <Select
                        placeholder="Gender"
                        value={store.userData.phase1.gender}
                        label="Gender"
                        values={[
                            {
                                label: 'Male',
                                value: 'male',
                            },
                            {
                                label: 'Female',
                                value: 'female',
                            },
                        ]}
                        onChange={(value) => {
                            phase.updateField('gender', value);
                        }}
                        onBlur={validateGender}
                        error={genderError}
                        className={classNames(styles.input, styles.genderSelect, {[styles.errorText]: genderError})}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <InputDatePicker
                        ariaLabel="date-of-birth"
                        value={phase.dateOfBirth}
                        onChange={(value) => {
                            phase.updateField('dateOfBirth', value);
                            validateDateOfBirth();
                        }}
                        onBlur={() => {
                            if (phase.dateOfBirth.trim().length !== 10)
                                setDateOfBirthErr('Please provide the answer in MM/DD/YYYY format');
                            else validateDateOfBirth();
                        }}
                        onKeyDown={phase.updateField('dateOfBirth')}
                        placeholder="MM/DD/YYYY"
                        label="Birth Date"
                        maxDate={dayjs()}
                        error={dateOfBirthErr}
                        className={classNames(styles.inputDate, {[styles.errorText]: dateOfBirthErr})}
                    />
                </Grid>
            </Grid>
            <CheckBox
                ariaLabel="Privacy policy"
                value={consent}
                onChange={setConsent}
                error={consentErr}
                noActionOnLabel
                wrapperClassName={classNames(styles.checkbox, {
                    [styles.checkBoxExtraMargin]: dateOfBirthErr || genderError,
                })}
            >
                {consentLabel}
            </CheckBox>
            <ButtonNext onClick={saveForm} disabled={!isNextButtonEnabled || isLoading} className={styles.nextButton} />
        </Stack>
    );
};

export default observer(LeadForm);
